import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Phone, MapPin, Search } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import Button from '../common/Button';
import Alert from '../common/Alert';
import { testimonialService } from '../../services/testimonialService';
import { contactService } from '../../services/contactService';
import banner from '../../static/images/banner.jpeg';

const LandingPage = () => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [shipmentForm, setShipmentForm] = useState({
    name: '',
    phone: '',
    from_location: '',
    to_location: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const useInViewOnce = () => {
    const ref = useRef(null);
    useEffect(() => {
      const el = ref.current;
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.add('animate-in');
            observer.unobserve(el);
          }
        },
        { threshold: 0.15 }
      );

      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    return ref;
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await testimonialService.getFeatured(6);
      setTestimonials(response.data.data || []);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % Math.ceil(testimonials.length / getSlidesPerView()));
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials]);

  const getSlidesPerView = () => {
    if (window.innerWidth >= 1024) return 3;
    if (window.innerWidth >= 768) return 2;
    return 1;
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.ceil(testimonials.length / getSlidesPerView()));
  };

  const handleScheduleShipment = async () => {
    if (!shipmentForm.name.trim()) {
      Alert.error('Please enter your name');
      return;
    }

    if (!shipmentForm.phone.trim()) {
      Alert.error('Please enter phone number');
      return;
    }

    if (shipmentForm.phone.trim().length < 10) {
      Alert.error('Please enter a valid phone number (minimum 10 digits)');
      return;
    }

    if (!shipmentForm.from_location.trim()) {
      Alert.error('Please enter from location');
      return;
    }

    if (!shipmentForm.to_location.trim()) {
      Alert.error('Please enter to location');
      return;
    }

    const confirmed = await Alert.confirm(
      'Are you sure you want to submit this shipment request?',
      'Confirm Shipment Request'
    );

    if (!confirmed) {
      return;
    }

    setSubmitting(true);
    try {
      await contactService.scheduleShipment(shipmentForm);
      Alert.success('Your shipment request has been submitted successfully. We will contact you soon.');
      setShipmentForm({
        name: '',
        phone: '',
        from_location: '',
        to_location: ''
      });
    } catch (error) {
      console.error('Error submitting shipment request:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit request. Please try again.';
      Alert.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.ceil(testimonials.length / getSlidesPerView())) % Math.ceil(testimonials.length / getSlidesPerView()));
  };

  const feature1Ref = useInViewOnce();
  const feature2Ref = useInViewOnce();
  const feature3Ref = useInViewOnce();
  const feature4Ref = useInViewOnce();

  const mobileAppRef = useInViewOnce();
  const mobileApp1Ref = useInViewOnce();
  const mobileApp2Ref = useInViewOnce();

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <svg
        key={index}
        className={`w-5 h-5 ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const handleFormChange = (field) => (e) => {
    setShipmentForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* WhatsApp Float Button */}
      <div className="fixed bottom-20 right-5 z-40">
        <a
          href="https://wa.me/919167107111?text=Hi%2C%20I%20would%20like%20to%20know%20more%20about%20your%20logistics%20services"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-medium hover:shadow-strong transition-all duration-300 flex items-center justify-center group"
          aria-label="Chat on WhatsApp"
        >
          <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
          </svg>
        </a>
      </div>

      <Navbar />

      {/* Hero Banner */}
      <section className="relative min-h-[480px] md:min-h-[520px] overflow-hidden pt-[72px] md:pt-[80px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${banner})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-dark/70 via-dark/50 to-dark/30" />

        <div className="relative z-10 container mx-auto px-4 lg:px-6 py-12 md:py-16">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-10">

            {/* Left Content */}
            <div className="w-full lg:max-w-xl text-center lg:text-left">
              <p className="text-brand-light text-sm font-medium uppercase tracking-widest mb-3">
                Metro Logistics
              </p>
              <h1 className="text-3xl md:text-5xl font-semibold text-white leading-tight">
                Delivering <span className="text-brand-light">Trust</span>
              </h1>
              <p className="mt-3 text-lg md:text-xl text-gray-200 font-medium">
                Delivering Growth
              </p>
              <div className="w-16 h-1 bg-brand rounded-full mt-4 mx-auto lg:mx-0" />
              <p className="mt-6 text-lg md:text-xl text-white font-medium tracking-wide">
                DAILY EXPRESS CARGO SERVICES
              </p>
              <div className="mt-4 text-gray-200 text-base space-y-1">
                <p>Mumbai – Hyderabad | Mumbai – Bangalore</p>
                <p>Mumbai – Pune | Mumbai – Telangana</p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Button
                  variant="primary"
                  size="lg"
                  icon={Search}
                  onClick={() => navigate('/public-track-shipment')}
                >
                  Track Shipment
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  icon={Phone}
                  onClick={() => navigate('/contact-us')}
                >
                  Contact Us
                </Button>
              </div>
            </div>

            {/* Schedule Shipment Card */}
            <div className="w-full lg:max-w-md lg:shrink-0">
              <div className="bg-white/95 backdrop-blur-sm rounded-card shadow-strong p-6 md:p-8 border border-white/20">
                <h2 className="text-xl font-semibold text-dark mb-6 flex items-center gap-2">
                  <Package className="w-5 h-5 text-brand" />
                  Schedule Shipment
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    className="input-clean"
                    placeholder="Your Name"
                    value={shipmentForm.name}
                    onChange={handleFormChange('name')}
                  />
                  <input
                    className="input-clean"
                    placeholder="Phone Number"
                    value={shipmentForm.phone}
                    onChange={handleFormChange('phone')}
                  />
                  <input
                    className="input-clean"
                    placeholder="From Location"
                    value={shipmentForm.from_location}
                    onChange={handleFormChange('from_location')}
                  />
                  <input
                    className="input-clean"
                    placeholder="To Location"
                    value={shipmentForm.to_location}
                    onChange={handleFormChange('to_location')}
                  />
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  className="mt-6 w-full"
                  loading={submitting}
                  onClick={handleScheduleShipment}
                >
                  Submit Request
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl md:text-2xl font-light text-dark leading-relaxed">
              &ldquo;Metro Logistics, founded by <span className="font-medium text-brand">Mustafa Shaikh</span>,
              is a trusted logistics and transportation company committed to delivering
              reliable, efficient, and timely cargo solutions.&rdquo;
            </p>
            <div className="w-16 h-0.5 bg-brand mx-auto my-6 rounded-full" />
            <p className="text-secondary text-lg leading-relaxed">
              Built on strong values of integrity, service, and operational excellence,
              Metro Logistics supports businesses with seamless movement of goods
              across locations.
            </p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
            <p className="text-brand font-semibold text-sm uppercase tracking-widest mb-3">
              Our Expertise & Key Features
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold text-dark leading-tight">
              Delivering Solutions, Powering Your Growth
            </h2>
            <div className="w-16 h-0.5 bg-brand mx-auto mt-5 rounded-full" />
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { num: '01', title: 'Unbeatable Rates', desc: 'Economical and competitive pricing ensuring maximum value for every shipment.' },
              { num: '02', title: 'Real-Time Connectivity', desc: 'Dedicated server and lease-line connectivity for real-time parcel tracking.' },
              { num: '03', title: 'Email Invoicing', desc: 'Monthly email invoicing with detailed transaction reports for contract clients.' },
              { num: '04', title: 'Bulk Delivery', desc: 'Nationwide bulk delivery solutions designed for high-volume shipments.' },
            ].map((feature, idx) => (
              <div
                key={feature.num}
                ref={[feature1Ref, feature2Ref, feature3Ref, feature4Ref][idx]}
                className="bg-white rounded-card shadow-card p-6 md:p-8 border border-gray-100 hover:shadow-soft transition-shadow"
              >
                <span className="block text-4xl font-light text-brand mb-3">{feature.num}</span>
                <h3 className="text-lg font-semibold text-dark mb-2">{feature.title}</h3>
                <p className="text-secondary leading-relaxed text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Features */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
            <p className="text-brand font-semibold text-sm uppercase tracking-widest mb-3">
              Technology Features
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold text-dark leading-tight">
              Powering logistics with intelligent technology
            </h2>
            <div className="w-16 h-0.5 bg-brand mx-auto mt-5 rounded-full" />
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {[
              {
                ref: mobileAppRef,
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                ),
                color: 'text-brand',
                bg: 'bg-brand-light',
                title: 'Mobile App Integration',
                desc: 'Seamless mobile experience for booking, tracking, and managing shipments with real-time visibility.',
              },
              {
                ref: mobileApp1Ref,
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2" />
                ),
                color: 'text-brand',
                bg: 'bg-brand-light',
                title: 'Automated Billing',
                desc: 'Smart automated invoicing system with accurate cost calculations and transparent billing workflows.',
              },
              {
                ref: mobileApp2Ref,
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8" />
                ),
                color: 'text-brand',
                bg: 'bg-brand-light',
                title: '24/7 Customer Support',
                desc: 'Always-available support through chat, phone, and email to keep logistics operations running smoothly.',
              },
            ].map((item) => (
              <div key={item.title} ref={item.ref} className="text-center space-y-4 p-6 rounded-card border border-gray-100 hover:shadow-card transition-shadow">
                <div className={`w-14 h-14 ${item.bg} rounded-card flex items-center justify-center mx-auto`}>
                  <svg className={`w-7 h-7 ${item.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {item.icon}
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-dark">{item.title}</h4>
                <p className="text-secondary leading-relaxed text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
